import { MAX_UPLOAD_BYTES, type UploadContentType } from '@alison/api/uploads';
import type { SettledUpload, UploadRow } from './uploadRows.js';

const UNKNOWN_FAILURE = 'the upload failed';
const READABLE: Record<string, string> = {
  upload_bucket_not_configured: 'uploads are not set up on this environment',
  UNAUTHORIZED: 'your session expired',
  FORBIDDEN: 'you do not have access to upload here',
  INTERNAL_SERVER_ERROR: UNKNOWN_FAILURE,
};

export interface UploadLink {
  uploadUrl: string;
  objectName: string;
}

export interface UploadOneDeps {
  createLink: (input: { contentType: UploadContentType; sizeBytes: number }) => Promise<UploadLink>;
  put: (url: string, file: File, contentType: UploadContentType) => Promise<void>;
}

function reasonOf(error: unknown): string {
  if (!(error instanceof Error) || error.message === '') return UNKNOWN_FAILURE;
  return READABLE[error.message] ?? error.message;
}

export async function putToSignedUrl(
  url: string,
  file: File,
  contentType: UploadContentType,
): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
      'x-goog-content-length-range': `0,${MAX_UPLOAD_BYTES}`,
    },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`the upload was refused (${response.status})`);
  }
}

export async function uploadOne(row: UploadRow, deps: UploadOneDeps): Promise<SettledUpload> {
  try {
    const link = await deps.createLink({
      contentType: row.contentType,
      sizeBytes: row.sizeBytes,
    });
    await deps.put(link.uploadUrl, row.file, row.contentType);
    return { objectName: link.objectName };
  } catch (error) {
    return { error: reasonOf(error) };
  }
}
